// client/src/services/socketClient.ts (TypeScript since ST-00216; behaviour unchanged)
//
// One place to open Socket.IO connections to the API (ST-00214; root cause of
// ST-00176, audit IAM-12 / TRK-5).
//
// URL: the socket lives next to the API, never on a hard-coded port. Pages used
// to dial `${hostname}:5002`, which nothing listens on behind the production
// proxy, so the live map never moved and the Driver PWA was always "Offline".
//   - REACT_APP_API_URL absolute (every build: dev .env.local, Docker build arg)
//     -> its origin, e.g. https://lexibridge.lex-adapt.com/api -> https://lexibridge.lex-adapt.com
//       (production: same origin; lex-adapt-proxy forwards /socket.io to the API)
//   - unset or relative -> the page origin (Vite dev server proxies /socket.io)
//
// Auth: the server authenticates every socket from the lb_access session cookie
// and puts it in its own tenant room (no client-chosen tenant). The access cookie
// lives 5 minutes; when a (re)connect is refused as 'unauthorized' the session is
// refreshed once through the shared single-flight refresh and the socket retries.
// If that fails, onAuthFailed(err) is called and the socket stays down.
//
// Lifetime: the server closes a socket when the access token it connected with
// expires (or the session is revoked). socket.io does not reconnect after a
// server-side disconnect, so this helper does, after a short delay, with the
// browser's current cookie (refreshing it once on 'unauthorized').
//
// 'unavailable': the server could not check the session (database down). The
// connection is refused, but it is not a lost session: retry with exponential
// backoff and jitter, no refresh, no onAuthFailed.

import { io, type Socket } from 'socket.io-client';
import { refreshSession } from './authClient';
import type { ConnectSocketOptions, SocketConnectError } from '../types/clientShell';

export type { ConnectSocketOptions, SocketConnectError };

const AUTH_ERRORS = new Set(['unauthorized', 'forbidden']);
const REFRESH_RETRY_GAP_MS = 30000;
const UNAVAILABLE_RETRY_BASE_MS = 2000;
const SERVER_DISCONNECT_RETRY_BASE_MS = 1000;
const RETRY_MAX_MS = 60000;
// A connection that lasted this long resets the backoff.
const STABLE_CONNECTION_MS = 30000;

export function getSocketBaseUrl(
  apiUrl: string | undefined = import.meta.env.REACT_APP_API_URL,
  pageOrigin: string = typeof window !== 'undefined' ? window.location.origin : ''
): string {
  if (apiUrl && /^https?:\/\//i.test(apiUrl)) {
    return apiUrl.replace(/\/+$/, '').replace(/\/api$/, '');
  }
  return pageOrigin;
}

/**
 * Open an authenticated Socket.IO connection.
 * @param namespace '' for the default namespace, or e.g. '/tracking'
 * @param options `onAuthFailed`, `refresh`, `random` (for tests: the jitter
 *   source), plus any socket.io-client option
 */
export function connectSocket(namespace = '', options: ConnectSocketOptions = {}): Socket {
  const { onAuthFailed, refresh = refreshSession, random = Math.random, ...ioOptions } = options;
  const socket = io(`${getSocketBaseUrl()}${namespace}`, {
    withCredentials: true,
    transports: ['websocket', 'polling'],
    ...ioOptions,
  });

  let lastRefresh = 0;
  let attempts = 0; // scheduled reconnects since the last stable connection
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let connectedAt = 0;
  let stopped = false; // the page closed the socket: never reconnect it

  const reconnectNow = () => {
    if (!stopped && !socket.connected) socket.connect();
  };
  const reconnectLater = (baseMs: number) => {
    if (retryTimer || stopped) return;
    const backoff = Math.min(baseMs * 2 ** attempts, RETRY_MAX_MS);
    attempts += 1;
    // Jitter (0.8x-1.2x) so every client does not reconnect at the same instant
    // after a database failover.
    const delay = Math.round(backoff * (0.8 + 0.4 * random()));
    retryTimer = setTimeout(() => {
      retryTimer = null;
      reconnectNow();
    }, delay);
  };

  // A pending retry must not resurrect a socket the page has closed.
  const closeSocket: (...args: unknown[]) => Socket = socket.disconnect.bind(socket);
  socket.disconnect = (...args: unknown[]) => {
    stopped = true;
    if (retryTimer) clearTimeout(retryTimer);
    retryTimer = null;
    return closeSocket(...args);
  };

  socket.on('connect', () => {
    connectedAt = Date.now();
  });

  socket.on('disconnect', (reason) => {
    if (connectedAt && Date.now() - connectedAt > STABLE_CONNECTION_MS) attempts = 0;
    // Other reasons (transport close, ping timeout) are retried by socket.io.
    if (reason === 'io server disconnect') reconnectLater(SERVER_DISCONNECT_RETRY_BASE_MS);
  });

  socket.on('connect_error', (err: SocketConnectError) => {
    if (!err) return;
    if (err.message === 'unavailable') {
      reconnectLater(UNAVAILABLE_RETRY_BASE_MS);
      return;
    }
    // A polling handshake with neither an Origin (same-origin GETs carry none)
    // nor a session cookie is refused by the server with HTTP 403 before any
    // namespace; treat it like 'unauthorized' (the access cookie has expired).
    const refused = err.description === 403;
    // Other transport errors: socket.io-client keeps retrying by itself.
    if (!refused && !AUTH_ERRORS.has(err.message)) return;
    // A refused handshake is not retried automatically. An expired access cookie
    // is the common case: refresh once, then reconnect.
    if (
      (refused || err.message === 'unauthorized') &&
      Date.now() - lastRefresh > REFRESH_RETRY_GAP_MS
    ) {
      lastRefresh = Date.now();
      Promise.resolve()
        .then(() => refresh())
        .then(reconnectNow)
        .catch(() => onAuthFailed && onAuthFailed(err));
      return;
    }
    if (onAuthFailed) onAuthFailed(err);
  });

  return socket;
}
